// ============================================================================
// MentionRichText.tsx — рендер простого текста сообщения с ПОДСВЕТКОЙ
// упоминаний «@login» участников и (опционально) совпадений ПОИСКА.
// Единый механизм ссылок платформы (RichText / HydraLink из lib/hydraLinks)
// НЕ дублируется: сначала текст разбирается на ссылки, и только внутри
// обычных кусков подсвечиваются упоминания/совпадения (внутри ссылок — нет).
// ============================================================================

import { useMemo, type ReactNode } from 'react';
import { HydraLink, RichText, tokenizeTextLinks } from '@/lib/hydraLinks';
import { mentionSegments } from '@/lib/mentions';
import { highlightSegments } from '@/lib/chatSearch';

interface MentionRichTextProps {
  text: string;
  /** логины УЧАСТНИКОВ диалога (подсвечиваются только они) */
  logins: readonly string[];
  /** мой логин — «упоминание вас» выделяется заметнее */
  myLogin?: string;
  /** сообщение моё (в своём пузыре текст уже акцентный — иной стиль чипа) */
  mine: boolean;
  /** совпадение поиска (подсветка найденного фрагмента в сообщении) */
  highlight?: string;
}

function mentionClass(mine: boolean, isMe: boolean): string {
  const base = mine
    ? 'font-bold underline decoration-primary/60 underline-offset-2'
    : 'font-semibold text-primary';
  return isMe ? `${base} rounded bg-primary/15 px-0.5 ring-1 ring-primary/30` : base;
}

function HighlightedChunk({ text, query }: { text: string; query: string }): ReactNode {
  if (!query) return text;
  return highlightSegments(text, query).map((seg, i) => (seg.match
    ? <mark key={i} className="rounded-sm bg-amber-300 px-0.5 text-amber-950">{seg.text}</mark>
    : <span key={i}>{seg.text}</span>));
}

export function MentionRichText({ text, logins, myLogin, mine, highlight = '' }: MentionRichTextProps) {
  const known = useMemo(() => new Set(logins.map((l) => l.toLowerCase())), [logins]);
  const me = (myLogin || '').toLowerCase();
  const query = highlight.trim();
  // Нет участников с логинами и нет поиска — обычный рендер ссылок (как раньше)
  if (known.size === 0 && !query) return <RichText text={text} />;
  const linkTokens = tokenizeTextLinks(text);
  return (
    <>
      {linkTokens.map((t, i) => t.link
        ? <HydraLink key={i} href={t.link}>{t.text}</HydraLink>
        : (
          <span key={i}>
            {mentionSegments(t.text, known).map((seg, j) => (seg.mention ? (
              <span
                key={j}
                className={mentionClass(mine, !!me && seg.login?.toLowerCase() === me)}
                title={me && seg.login?.toLowerCase() === me ? 'Здесь упомянули вас' : `Упоминание @${seg.login}`}
              >
                {seg.text}
              </span>
            ) : (
              <HighlightedChunk key={j} text={seg.text} query={query} />
            )))}
          </span>
        ))}
    </>
  );
}
